<template>
  <div class="MemberProfileCard" data-e2e="memberProfileCard">
    <div v-if="isModerator" class="MemberProfileCard__card">
      <div class="MemberProfileCard__top">
        <LogoRound class="MemberProfileCard__avatar" />
        <div class="MemberProfileCard__info">
          <div class="MemberProfileCard__name MemberProfileCard__name_moderator">
            {{ $t('forum.member_profile.moderator_name') }}
          </div>
          <div class="MemberProfileCard__role">
            {{ $t('forum.member_profile.moderator_role') }}
          </div>
        </div>
      </div>
      <div class="MemberProfileCard__body">
        <div class="MemberProfileCard__bio">
          {{ $t('forum.member_profile.moderator_line') }}
        </div>
      </div>
    </div>

    <div v-else-if="isAnonymous" class="MemberProfileCard__card">
      <div class="MemberProfileCard__top">
        <div class="MemberProfileCard__avatar MemberProfileCard__avatar_anon">
          {{ anonymousLetter }}
        </div>
        <div class="MemberProfileCard__info">
          <div class="MemberProfileCard__name">{{ anonymousName }}</div>
          <div class="MemberProfileCard__role">
            {{ $t('forum.member_profile.anonymous_role') }}
          </div>
        </div>
      </div>
      <div class="MemberProfileCard__body">
        <div class="MemberProfileCard__empty">
          {{ $t('forum.member_profile.anonymous_notice') }}
        </div>
      </div>
    </div>

    <div v-else class="MemberProfileCard__card">
      <div class="MemberProfileCard__top">
        <div v-if="loading" class="MemberProfileCard__avatar MemberProfileCard__avatar_placeholder"></div>
        <div v-else-if="userModel" class="MemberProfileCard__avatar">
          <UserAvatar :user="userModel" />
        </div>
        <ProspectAvatar
          v-else-if="prospectModel"
          class="MemberProfileCard__avatar"
          :prospect="prospectModel"
          disable-tooltip
          disable-new-sms-indicator
        />
        <div class="MemberProfileCard__info">
          <LLLoader v-if="loading" left />
          <template v-else>
            <div class="MemberProfileCard__name">{{ displayName }}</div>
            <div class="MemberProfileCard__role">{{ $t('forum.member_profile.member_role') }}</div>
          </template>
        </div>
      </div>
      <!-- A staff user has no chat-scoped profile, so there are no details to show. -->
      <div v-if="!loading && !isUser" class="MemberProfileCard__body">
        <div v-if="profile.location" class="MemberProfileCard__row">
          <span class="MemberProfileCard__key">{{ $t('forum.member_profile.location') }}</span>
          <span class="MemberProfileCard__value">{{ profile.location }}</span>
        </div>
        <div v-if="profile.ageRange" class="MemberProfileCard__row">
          <span class="MemberProfileCard__key">{{ $t('forum.member_profile.age') }}</span>
          <span class="MemberProfileCard__value">{{ ageRangeLabel }}</span>
        </div>
        <div v-if="profile.occupation" class="MemberProfileCard__row">
          <span class="MemberProfileCard__key">{{ $t('forum.member_profile.does') }}</span>
          <span class="MemberProfileCard__value">{{ profile.occupation }}</span>
        </div>
        <div v-if="profile.aboutMe" class="MemberProfileCard__bio">{{ profile.aboutMe }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import LogoRound from '@/assets/icons/LogoRound.vue'
import ProspectAvatar from '@/components/modules/prospects/ProspectAvatar.vue'
import UserAvatar from '@/components/modules/users/UserAvatar.vue'
import LLLoader from '@/components/common/LLLoader'
import { ProspectModel } from '@/models/prospect'
import { UserModel } from '@/models/user'
import { splitAgeBandLabel } from '@/utils/ageGroups'

/**
 * Member profile card shown inside an LLPopper anchored to a member's avatar or mention
 * (see ForumResponseAvatar / ForumChatThreadCardHeaderProfile / ForumResponseBodyTextOutputMention).
 * It loads the profile on mount, so it only fetches while the popper is open. A staff user is a chat
 * member too: the API resolves them to a reduced profile (`isUser`), which drops the details block.
 */
export default {
  name: 'MemberProfileCard',
  components: { LogoRound, ProspectAvatar, UserAvatar, LLLoader },
  props: {
    chatId: { type: [Number, String], default: null },
    memberId: { type: [String, Number], default: null },
    moderator: { type: Boolean, default: false },
    // Anonymous members have no fetchable profile: the id alone renders the static card.
    anonymousId: { type: String, default: null }
  },
  data() {
    return {
      profile: {},
      loading: false,
      anonymousFromApi: false
    }
  },
  computed: {
    isModerator() {
      return this.moderator
    },
    isAnonymous() {
      return !!this.anonymousId || this.anonymousFromApi
    },
    // A staff user (no chat-scoped profile) comes back flagged by the API and renders as a member.
    isUser() {
      return !!this.profile.isUser
    },
    // The API returns the same identity shape either way, so the avatar can be built from the profile.
    userModel() {
      return this.isUser ? UserModel.parseFromApi({ user: this.profile }) : null
    },
    displayName() {
      const first = this.profile.firstName || ''
      const lastInitial = this.profile.lastName || ''
      return [first, lastInitial].filter(Boolean).join(' ') || this.$t('not-available')
    },
    anonymousName() {
      return this.anonymousId
        ? this.$t('forum.response.anonymous_reply_user_name_with_id', { id: this.anonymousId })
        : this.$t('forum.response.anonymous_reply_user_name')
    },
    anonymousLetter() {
      return this.$t('forum.response.anonymous_reply_user_name').slice(0, 1)
    },
    ageRangeLabel() {
      const band = this.profile?.ageRange
      const range = splitAgeBandLabel(band)
      return range ? this.$t('forum.member_profile.age_range', range) : band
    },
    prospectModel() {
      if (!this.profile?.firstName && !this.profile?.image) return null
      return ProspectModel.parseFromApi({
        prospect: {
          id: this.profile.id,
          firstName: this.profile.firstName,
          lastName: this.profile.lastName,
          image: this.profile.image
        }
      })
    }
  },
  mounted() {
    this.fetchProfile()
  },
  methods: {
    async fetchProfile() {
      if (this.isModerator || this.anonymousId || !this.chatId || !this.memberId) return

      this.loading = true
      try {
        const { profile } = await this.$api.forum.chats.getMemberProfile({
          chatId: this.chatId,
          memberId: this.memberId
        })
        if (profile?.isAnonymous) {
          this.anonymousFromApi = true
          return
        }
        this.profile = profile || {}
      } catch (e) {
        this.$errorAlert(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.MemberProfileCard {
  @apply p-4 text-left;
  width: 288px;
  max-width: calc(100vw - 32px);

  &__card {
    @apply w-full;
  }
  &__top {
    @apply flex items-center;
  }
  &__avatar {
    @apply w-12 h-12 rounded-full mr-4 flex-shrink-0;
    &_anon {
      @apply bg-neutral-01-400 text-white flex items-center justify-center font-bold;
    }
    &_placeholder {
      @apply bg-neutral-01-200;
    }
  }
  &__info {
    @apply min-w-0 flex-1;
  }
  &__name {
    @apply text-subheading-01 text-primary-01 truncate;
    // Same accent as the moderator name in posts and thread cards.
    &_moderator {
      @apply text-accent-01;
    }
  }
  &__role {
    @apply text-caption-01 text-neutral-01-400;
    // `text-caption-01` bakes in semibold; the role line under the name stays plain.
    font-weight: theme('fontWeight.normal');
  }
  &__body {
    @apply mt-4;
  }
  &__row {
    @apply flex py-1;
  }
  &__key {
    @apply text-caption-01 text-neutral-01-400 uppercase mr-3 flex-shrink-0 w-20;
  }
  &__value {
    @apply text-body-03 text-primary-01;
  }
  &__bio {
    @apply text-body-03 mt-2 break-words;
  }
  &__empty {
    @apply text-body-03 text-neutral-01-400 italic;
  }
}
</style>
